import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import * as actions from '../../actions/generalActions';
import HeroStrip from './components/HeroStrip';
import RevenueTrendCard from './components/RevenueTrendCard';
import CashflowCard from './components/CashflowCard';
import AvgDealSizeCard from './components/AvgDealSizeCard';
import LeadTimeCard from './components/LeadTimeCard';
import SalesFunnelCard from './components/SalesFunnelCard';
import AgingBoardCard from './components/AgingBoardCard';
import ActivityHeatmapCard from './components/ActivityHeatmapCard';
import EntityStatusGrid from './components/EntityStatusGrid';
import ProductionPipelineCard from './components/ProductionPipelineCard';
import TopClientsCard from './components/TopClientsCard';
import BottlenecksCard from './components/BottlenecksCard';
import ProductionFunnelCard from './components/ProductionFunnelCard';
import CycleTimeCard from './components/CycleTimeCard';
import DateRangeControl, { CompareMode, PresetKey } from './components/DateRangeControl';
import FilterBar, { FilterState } from './components/FilterBar';
import InsightsCard from './components/InsightsCard';
import SavedViewsBar from './components/SavedViewsBar';
import LiveIndicator from './components/LiveIndicator';
import GuidedTour, { TourStep } from './components/GuidedTour';
import { useOverviewData } from './hooks/useOverviewData';
import { useFilterOptions } from './hooks/useFilterOptions';
import { useIsMobile } from './hooks/useIsMobile';
import { SavedView } from './utils/savedViews';
import './analyticsV2.scss';

// Inline SVG icons for section headers — small, colored anchors that give the
// eye three distinct landmarks ("snapshot / trends / operations") instead of
// three identical slate labels.
const SnapshotIcon: React.FC = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polyline points="3 12 7 12 10 5 14 19 17 12 21 12" />
    </svg>
);
const TrendsIcon: React.FC = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polyline points="3 17 9 11 13 15 21 7" />
        <polyline points="14 7 21 7 21 14" />
    </svg>
);
const OperationsIcon: React.FC = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.7 1.7 0 0 0 .34 1.87l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.7 1.7 0 0 0-1.87-.34 1.7 1.7 0 0 0-1.03 1.56V21a2 2 0 1 1-4 0v-.09a1.7 1.7 0 0 0-1.11-1.56 1.7 1.7 0 0 0-1.87.34l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.7 1.7 0 0 0 4.6 15a1.7 1.7 0 0 0-1.56-1.03H3a2 2 0 1 1 0-4h.09A1.7 1.7 0 0 0 4.6 9a1.7 1.7 0 0 0-.34-1.87l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.7 1.7 0 0 0 1.87.34H9a1.7 1.7 0 0 0 1.03-1.56V3a2 2 0 1 1 4 0v.09a1.7 1.7 0 0 0 1.03 1.56 1.7 1.7 0 0 0 1.87-.34l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.7 1.7 0 0 0-.34 1.87V9a1.7 1.7 0 0 0 1.56 1.03H21a2 2 0 1 1 0 4h-.09a1.7 1.7 0 0 0-1.51 1.03z" />
    </svg>
);
const SalesIcon: React.FC = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3 4h18l-3 9H6L3 4z" />
        <path d="M3 4l-1-2" />
        <circle cx="9" cy="20" r="1.5" />
        <circle cx="17" cy="20" r="1.5" />
    </svg>
);
const InfoIcon: React.FC = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="11" x2="12" y2="17" />
        <line x1="12" y1="7" x2="12.01" y2="7" />
    </svg>
);

const TOUR_SEEN_KEY = 'av2.tour.seen.v1';

const TOUR_STEPS: TourStep[] = [
    {
        target: 'header',
        titleId: 'analyticsV2.tour.header.title',
        titleDefault: 'Welcome to your Analitika hub',
        bodyId: 'analyticsV2.tour.header.body',
        bodyDefault:
            "Everything you need to know about your business, at a glance. The live indicator shows when the data was last refreshed — click the info button anytime to retake this tour.",
    },
    {
        target: 'controls',
        titleId: 'analyticsV2.tour.controls.title',
        titleDefault: 'Pick a period, compare, filter',
        bodyId: 'analyticsV2.tour.controls.body',
        bodyDefault:
            'Choose your date range, compare against the previous period or last year, and narrow the view to specific clients, cities, or production stages. Save your favourite views with one click.',
    },
    {
        target: 'insights',
        titleId: 'analyticsV2.tour.insights.title',
        titleDefault: 'Smart callouts',
        bodyId: 'analyticsV2.tour.insights.body',
        bodyDefault:
            "The most important shifts in your data — significant changes, rework spikes, and concentration risks — surfaced automatically so you don't have to hunt for them.",
    },
    {
        target: 'snapshot',
        titleId: 'analyticsV2.tour.snapshot.title',
        titleDefault: 'Your business right now',
        bodyId: 'analyticsV2.tour.snapshot.body',
        bodyDefault:
            'Headline KPIs with period-over-period changes, plus a live snapshot of every module — quickly see where every offer, agreement, and product stands.',
    },
    {
        target: 'trends',
        titleId: 'analyticsV2.tour.trends.title',
        titleDefault: 'How things change over time',
        bodyId: 'analyticsV2.tour.trends.body',
        bodyDefault:
            'Revenue trends, cashflow health, average deal size, and end-to-end lead time — the four metrics that tell you whether your financial momentum is healthy.',
    },
    {
        target: 'sales',
        titleId: 'analyticsV2.tour.sales.title',
        titleDefault: 'From first measure to signed contract',
        bodyId: 'analyticsV2.tour.sales.body',
        bodyDefault:
            'Watch your leads flow from measure to offer to agreement, with your headline close rate front and centre. See where deals convert and where they slip away.',
    },
    {
        target: 'operations',
        titleId: 'analyticsV2.tour.operations.title',
        titleDefault: 'Where the work happens',
        bodyId: 'analyticsV2.tour.operations.body',
        bodyDefault:
            'When work actually gets done, your top clients and biggest bottlenecks, production funnel and cycle times, and a live list of products that need attention right now.',
    },
];

const DAY_MS = 86_400_000;

const toYmd = (d: Date): string => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const parseIntList = (s: string | null): number[] =>
    s
        ? s
              .split(',')
              .map((x) => parseInt(x, 10))
              .filter((x) => Number.isFinite(x) && x > 0)
        : [];

const parseStringList = (s: string | null): string[] =>
    s ? s.split(',').map((x) => x.trim()).filter(Boolean) : [];

const parseDate = (s: string | null): Date | null => {
    if (!s) return null;
    const d = new Date(s + 'T00:00:00');
    return isNaN(d.getTime()) ? null : d;
};

const readInitialState = () => {
    if (typeof window === 'undefined') {
        return {
            preset: '30d' as PresetKey,
            customFrom: null as Date | null,
            customTo: null as Date | null,
            compareMode: 'previous' as CompareMode,
            compareCustomFrom: null as Date | null,
            compareCustomTo: null as Date | null,
            filters: { clientIds: [], cities: [], responsibilityIds: [] } as FilterState,
        };
    }
    const sp = new URLSearchParams(window.location.search);
    const preset = (sp.get('preset') as PresetKey) || '30d';
    const validPreset: PresetKey = ['7d', '30d', '90d', '12m', 'custom'].includes(preset)
        ? preset
        : '30d';
    const compare = (sp.get('compare') as CompareMode) || 'previous';
    const validCompare: CompareMode = ['previous', 'last_year', 'custom', 'off'].includes(compare)
        ? compare
        : 'previous';
    return {
        preset: validPreset,
        customFrom: parseDate(sp.get('cfrom')),
        customTo: parseDate(sp.get('cto')),
        compareMode: validCompare,
        compareCustomFrom: parseDate(sp.get('ccfrom')),
        compareCustomTo: parseDate(sp.get('ccto')),
        filters: {
            clientIds: parseIntList(sp.get('client')),
            cities: parseStringList(sp.get('city')),
            responsibilityIds: parseIntList(sp.get('resp')),
        },
    };
};

/**
 * Analitika V2 — light-theme, story-led companion to /dashboard/analytics.
 *
 * Phase 4: full interactivity.
 *   - Date range presets + custom range + comparison-mode selector
 *   - Client / City / Stage filter chips with multi-select popovers
 *   - State synced to URL (?preset=...&compare=...&client=...) so views are shareable
 *   - All filters apply to both primary (KPI) and trend (chart) fetches
 *   - Trend window stays at 12 months regardless of the active preset
 */
const AnalyticsPageV2: React.FC = () => {
    const intl = useIntl();
    const dispatch = useDispatch();

    // Mirrors every other module page so navbar permissions are present on
    // a hard reload of this route. App.tsx also dispatches at boot — both
    // paths exist intentionally (boot covers reload, this covers SPA-nav
    // refresh-on-mount). Bug history: NavbarV2 collapsed to zero modules
    // when this dispatch was missing.
    useEffect(() => {
        dispatch((actions as any).getUserPermissionsAndResponsibilities());
    }, [dispatch]);

    const initial = useMemo(readInitialState, []);
    const [preset, setPreset] = useState<PresetKey>(initial.preset);
    const [customFrom, setCustomFrom] = useState<Date | null>(initial.customFrom);
    const [customTo, setCustomTo] = useState<Date | null>(initial.customTo);
    const [compareMode, setCompareMode] = useState<CompareMode>(initial.compareMode);
    const [compareCustomFrom, setCompareCustomFrom] = useState<Date | null>(initial.compareCustomFrom);
    const [compareCustomTo, setCompareCustomTo] = useState<Date | null>(initial.compareCustomTo);
    const [filters, setFilters] = useState<FilterState>(initial.filters);
    const [tourOpen, setTourOpen] = useState(false);
    const isMobile = useIsMobile(720);
    // Default rows for the long leaderboards. On mobile, 8 stacked cards
    // each = a wall of scroll; 3 is enough to be glanceable, "Show all"
    // still reveals up to 10 (and the funnel reveals everything).
    const leaderboardDefaultRows = isMobile ? 3 : 8;

    // Compute the primary date window from the preset + compare mode.
    const primaryRange = useMemo(() => {
        const now = new Date();
        let from: Date;
        let to: Date;
        switch (preset) {
            case '7d':
                to = now;
                from = new Date();
                from.setDate(now.getDate() - 6);
                break;
            case '90d':
                to = now;
                from = new Date();
                from.setDate(now.getDate() - 89);
                break;
            case '12m':
                to = now;
                from = new Date();
                from.setMonth(now.getMonth() - 11);
                from.setDate(1);
                break;
            case 'custom':
                if (customFrom && customTo) {
                    from = customFrom;
                    to = customTo;
                } else {
                    // Fallback to 30d if custom dates are incomplete
                    to = now;
                    from = new Date();
                    from.setDate(now.getDate() - 29);
                }
                break;
            case '30d':
            default:
                to = now;
                from = new Date();
                from.setDate(now.getDate() - 29);
                break;
        }

        let compareFrom: Date | undefined;
        let compareTo: Date | undefined;
        if (compareMode === 'previous') {
            const days = Math.max(1, Math.round((to.getTime() - from.getTime()) / DAY_MS) + 1);
            compareTo = new Date(from);
            compareTo.setDate(from.getDate() - 1);
            compareFrom = new Date(compareTo);
            compareFrom.setDate(compareTo.getDate() - (days - 1));
        } else if (compareMode === 'last_year') {
            compareFrom = new Date(from);
            compareFrom.setFullYear(from.getFullYear() - 1);
            compareTo = new Date(to);
            compareTo.setFullYear(to.getFullYear() - 1);
        } else if (compareMode === 'custom' && compareCustomFrom && compareCustomTo) {
            // Only kick off compare queries once both dates are filled in. If
            // either is null we skip — equivalent to 'off' for the fetch, so
            // the user sees current-period data without misleading deltas.
            compareFrom = compareCustomFrom;
            compareTo = compareCustomTo;
        }
        return { from, to, compareFrom, compareTo };
    }, [preset, customFrom, customTo, compareMode, compareCustomFrom, compareCustomTo]);

    // Trend window is always last 12 full months — independent of the primary
    // window so the area chart is consistently useful.
    const trendRange = useMemo(() => {
        const to = new Date();
        const from = new Date();
        from.setMonth(to.getMonth() - 11);
        from.setDate(1);
        return { from, to };
    }, []);

    // Sync state to URL so refresh + share both work.
    useEffect(() => {
        const sp = new URLSearchParams();
        if (preset !== '30d') sp.set('preset', preset);
        if (preset === 'custom') {
            if (customFrom) sp.set('cfrom', toYmd(customFrom));
            if (customTo) sp.set('cto', toYmd(customTo));
        }
        if (compareMode !== 'previous') sp.set('compare', compareMode);
        if (compareMode === 'custom') {
            if (compareCustomFrom) sp.set('ccfrom', toYmd(compareCustomFrom));
            if (compareCustomTo) sp.set('ccto', toYmd(compareCustomTo));
        }
        if (filters.clientIds.length) sp.set('client', filters.clientIds.join(','));
        if (filters.cities.length) sp.set('city', filters.cities.join(','));
        if (filters.responsibilityIds.length) sp.set('resp', filters.responsibilityIds.join(','));
        const q = sp.toString();
        const newUrl = q ? `${window.location.pathname}?${q}` : window.location.pathname;
        window.history.replaceState(null, '', newUrl);
    }, [preset, customFrom, customTo, compareMode, compareCustomFrom, compareCustomTo, filters]);

    const filterParams = useMemo(
        () => ({
            clientIds: filters.clientIds,
            cities: filters.cities,
            responsibilityIds: filters.responsibilityIds,
        }),
        [filters],
    );

    // Poll the primary window so the LiveIndicator's "Updated Ns ago" stays
    // truthful. 2 min mostly returns the warm 5-min server cache (cheap), and
    // the hook only re-renders when the snapshot actually changes. The trend
    // window is a frozen 12-month range, so it doesn't need polling.
    const OVERVIEW_POLL_MS = 120_000;
    const { data, loading, error } = useOverviewData(
        { ...primaryRange, filters: filterParams },
        OVERVIEW_POLL_MS,
    );
    const { data: trendData, loading: trendLoading } = useOverviewData({
        ...trendRange,
        filters: filterParams,
    });
    const { options: filterOptions } = useFilterOptions();

    // First-visit auto-open. Triggers once after the initial data load so the
    // page is fully laid out before the tour starts measuring rects. Dismissal
    // (or completion) writes the flag and the tour never auto-opens again.
    useEffect(() => {
        if (typeof window === 'undefined') return;
        if (loading || !data) return;
        try {
            if (window.localStorage.getItem(TOUR_SEEN_KEY)) return;
        } catch (_) { return; }
        const t = window.setTimeout(() => setTourOpen(true), 600);
        return () => window.clearTimeout(t);
    }, [loading, data]);

    const handleApplyView = (view: SavedView) => {
        setPreset(view.preset);
        setCustomFrom(view.customFrom ? new Date(view.customFrom + 'T00:00:00') : null);
        setCustomTo(view.customTo ? new Date(view.customTo + 'T23:59:59') : null);
        setCompareMode(view.compareMode);
        setCompareCustomFrom(
            view.compareCustomFrom ? new Date(view.compareCustomFrom + 'T00:00:00') : null,
        );
        setCompareCustomTo(
            view.compareCustomTo ? new Date(view.compareCustomTo + 'T23:59:59') : null,
        );
        setFilters(view.filters);
    };

    return (
        <div className="av2-page">
            <div className="av2-page__mesh" aria-hidden="true" />

            <header className="av2-header">
                <div className="av2-header__text">
                    <h1 className="av2-title">
                        <FormattedMessage id="analyticsV2.title" defaultMessage="Analitika" />
                    </h1>
                    <p className="av2-subtitle">
                        <FormattedMessage
                            id="analyticsV2.subtitle"
                            defaultMessage="Çdo gjë që ju duhet të dini për biznesin tuaj, në një vështrim."
                        />
                    </p>
                </div>
                <div className="av2-header__status" data-tour="header">
                    <LiveIndicator generatedAt={data?.generated_at ?? null} />
                    <button
                        type="button"
                        className="av2-info-btn"
                        onClick={() => setTourOpen(true)}
                        aria-label={intl.formatMessage({
                            id: 'analyticsV2.tour.openButton',
                            defaultMessage: 'Take a tour',
                        })}
                        title={intl.formatMessage({
                            id: 'analyticsV2.tour.openButton',
                            defaultMessage: 'Take a tour',
                        })}
                    >
                        <InfoIcon />
                    </button>
                </div>
            </header>

            <div data-tour="controls">
                <SavedViewsBar
                    currentState={{
                        preset, customFrom, customTo,
                        compareMode, compareCustomFrom, compareCustomTo,
                        filters,
                    }}
                    onApply={handleApplyView}
                />

                <DateRangeControl
                    preset={preset}
                    customFrom={customFrom}
                    customTo={customTo}
                    compareMode={compareMode}
                    compareCustomFrom={compareCustomFrom}
                    compareCustomTo={compareCustomTo}
                    onPresetChange={(next) => {
                        setPreset(next);
                        if (next === 'custom' && (!customFrom || !customTo)) {
                            const now = new Date();
                            const start = new Date();
                            start.setDate(now.getDate() - 29);
                            setCustomFrom(start);
                            setCustomTo(now);
                        }
                    }}
                    onCustomDatesChange={(from, to) => {
                        setCustomFrom(from);
                        setCustomTo(to);
                    }}
                    onCompareModeChange={(next) => {
                        setCompareMode(next);
                        // Seed custom compare with the period immediately before the
                        // current primary range — most useful default + lets the user
                        // see a comparison without filling anything in.
                        if (next === 'custom' && (!compareCustomFrom || !compareCustomTo)) {
                            const { from, to } = primaryRange;
                            const days = Math.max(1, Math.round((to.getTime() - from.getTime()) / DAY_MS) + 1);
                            const seedTo = new Date(from);
                            seedTo.setDate(from.getDate() - 1);
                            const seedFrom = new Date(seedTo);
                            seedFrom.setDate(seedTo.getDate() - (days - 1));
                            setCompareCustomFrom(seedFrom);
                            setCompareCustomTo(seedTo);
                        }
                    }}
                    onCompareCustomDatesChange={(from, to) => {
                        setCompareCustomFrom(from);
                        setCompareCustomTo(to);
                    }}
                />

                <FilterBar options={filterOptions} value={filters} onChange={setFilters} />
            </div>

            <main className="av2-canvas">
                <div data-tour="insights">
                    <InsightsCard data={data} />
                </div>

                <div data-tour="snapshot">
                    <h2 className="av2-section-title av2-section-title--snapshot">
                        <span className="av2-section-title__icon"><SnapshotIcon /></span>
                        <FormattedMessage id="analyticsV2.section.snapshot" defaultMessage="Snapshot" />
                    </h2>
                    <HeroStrip
                        data={data}
                        revenueTrend={trendData?.revenue_by_month ?? null}
                        loading={loading}
                        error={error}
                    />
                    <ProductionPipelineCard data={data?.production_pipeline ?? null} loading={loading} />
                    <EntityStatusGrid
                        entries={data?.status_matrix ?? null}
                        previous={data?.status_matrix_previous ?? null}
                        loading={loading}
                    />
                </div>

                <div data-tour="trends">
                    <h2 className="av2-section-title av2-section-title--trends">
                        <span className="av2-section-title__icon"><TrendsIcon /></span>
                        <FormattedMessage id="analyticsV2.section.trends" defaultMessage="Trends" />
                    </h2>
                    <div className="av2-trends-grid">
                        <RevenueTrendCard data={trendData} loading={trendLoading} locale={intl.locale} />
                        <CashflowCard
                            cashflow={data?.cashflow ?? null}
                            cashflowPrevious={data?.cashflow_previous ?? null}
                            loading={loading}
                        />
                    </div>
                    <div className="av2-grid-2col">
                        <AvgDealSizeCard
                            revenue={data?.pulse?.revenue ?? null}
                            cashflow={data?.cashflow ?? null}
                            cashflowPrevious={data?.cashflow_previous ?? null}
                            loading={loading}
                        />
                        <LeadTimeCard
                            data={data?.lead_time ?? null}
                            previous={data?.lead_time_previous ?? null}
                            loading={loading}
                        />
                    </div>
                </div>

                <div data-tour="sales">
                    <h2 className="av2-section-title av2-section-title--sales">
                        <span className="av2-section-title__icon"><SalesIcon /></span>
                        <FormattedMessage id="analyticsV2.section.sales" defaultMessage="Sales" />
                    </h2>
                    <SalesFunnelCard
                        data={data?.sales_funnel ?? null}
                        previous={data?.sales_funnel_previous ?? null}
                        loading={loading}
                    />
                </div>

                <div data-tour="operations">
                    <h2 className="av2-section-title av2-section-title--operations">
                        <span className="av2-section-title__icon"><OperationsIcon /></span>
                        <FormattedMessage
                            id="analyticsV2.section.operations"
                            defaultMessage="Operations"
                        />
                    </h2>
                    <ActivityHeatmapCard
                        cells={data?.activity_heatmap ?? null}
                        loading={loading}
                    />
                    <div className="av2-grid-2col">
                        <TopClientsCard
                            clients={data?.top_clients ?? null}
                            clientsPrevious={data?.top_clients_previous ?? null}
                            loading={loading}
                            defaultRows={leaderboardDefaultRows}
                        />
                        <BottlenecksCard
                            entries={data?.rework_leaderboard ?? null}
                            entriesPrevious={data?.rework_leaderboard_previous ?? null}
                            loading={loading}
                            defaultRows={leaderboardDefaultRows}
                        />
                    </div>
                    <div className="av2-grid-2col">
                        <ProductionFunnelCard
                            stages={data?.funnel ?? null}
                            previousStages={data?.funnel_previous ?? null}
                            loading={loading}
                            defaultRows={leaderboardDefaultRows}
                        />
                        <CycleTimeCard
                            distributions={data?.cycle_time_distributions ?? null}
                            loading={loading}
                        />
                    </div>
                    <AgingBoardCard
                        products={data?.aging_products ?? null}
                        loading={loading}
                    />
                </div>
            </main>

            <GuidedTour
                steps={TOUR_STEPS}
                open={tourOpen}
                onClose={() => {
                    setTourOpen(false);
                    try { window.localStorage.setItem(TOUR_SEEN_KEY, '1'); } catch (_) {}
                }}
            />
        </div>
    );
};

export default AnalyticsPageV2;
